// Admin-instellingenscherm. Voor v1 bevat het alleen het beheer van het
// site-logo (boven het tekenen-paneel). Open vanuit de scenario-popover
// of een aparte topbar-knop.

import { useState, useRef } from "react";
import { Modal } from "./ui/Modal";
import { Button } from "./ui/Button";
import { useStore } from "../state/store";

interface Props {
  open: boolean;
  onClose: () => void;
}

const MAX_LOGO_BYTES = 60_000;

export function AdminSettingsDialog({ open, onClose }: Props) {
  const logoUrl = useStore((s) => s.logoUrl);
  const setLogoUrl = useStore((s) => s.setLogoUrl);

  const [draft, setDraft] = useState<string>(logoUrl ?? "");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  // Sync draft als de modal opnieuw open gaat met een gewijzigde waarde.
  if (open && draft === "" && logoUrl) {
    setDraft(logoUrl);
  }

  async function handleFile(files: FileList | null) {
    setError(null);
    if (!files || files.length === 0) return;
    const f = files[0];
    if (!f.type.startsWith("image/")) {
      setError("Geen geldig afbeeldingsbestand.");
      return;
    }
    if (f.size > MAX_LOGO_BYTES) {
      setError(
        `Bestand te groot (${Math.round(f.size / 1024)} KB). Maximum is ${Math.round(MAX_LOGO_BYTES / 1024)} KB; comprimeer of gebruik SVG.`,
      );
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setDraft(String(reader.result));
    reader.onerror = () => setError("Bestand lezen mislukt.");
    reader.readAsDataURL(f);
  }

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const trimmed = draft.trim();
      await setLogoUrl(trimmed.length > 0 ? trimmed : null);
      onClose();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Opslaan mislukt — probeer opnieuw.",
      );
    } finally {
      setSaving(false);
    }
  }

  async function removeLogo() {
    setSaving(true);
    setError(null);
    try {
      await setLogoUrl(null);
      setDraft("");
      onClose();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Verwijderen mislukt — probeer opnieuw.",
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Site-instellingen (admin)" width="34rem">
      <div className="space-y-4">
        <section>
          <h3 className="text-sm font-semibold mb-2">Logo</h3>
          <p className="text-xs text-muted-foreground mb-3">
            Wordt boven de tekenen-tools getoond. Upload een PNG, JPG of SVG
            (max ~60 KB) of plak een URL.
          </p>

          <div className="flex items-start gap-3 mb-3">
            <div
              className="w-24 h-16 border border-input rounded-md bg-secondary flex items-center justify-center overflow-hidden flex-none"
              aria-label="Voorbeeld"
            >
              {draft ? (
                <img
                  src={draft}
                  alt="logo voorbeeld"
                  className="max-w-full max-h-full object-contain"
                />
              ) : (
                <span className="text-[10px] text-muted-foreground">
                  geen logo
                </span>
              )}
            </div>
            <div className="flex-1 flex flex-col gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileRef.current?.click()}
              >
                Kies bestand
              </Button>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleFile(e.target.files)}
              />
              <input
                type="text"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="https://… of data:image/…"
                className="w-full bg-background border border-input rounded-md px-2 py-1 text-sm font-mono"
              />
            </div>
          </div>
        </section>

        {error && (
          <div className="text-xs text-destructive bg-rose-50 border border-rose-200 rounded-md p-2">
            {error}
          </div>
        )}

        <div className="flex justify-between gap-2 pt-2 border-t border-border">
          <Button
            variant="ghost"
            size="sm"
            onClick={removeLogo}
            disabled={saving || !logoUrl}
          >
            Verwijder logo
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>
              Annuleer
            </Button>
            <Button size="sm" onClick={save} disabled={saving}>
              {saving ? "Bezig…" : "Opslaan"}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
